/**
 * Singleton G-code parser web worker.
 *
 * Manages a single background worker for parsing G-code metadata,
 * handling request/response pairs via a Promise map keyed by fileId.
 * The worker is lazily created on first call and terminated when done.
 */

import { GcodeExtractedData } from './gcodeParser';

type Pending = {
  resolve: (data: GcodeExtractedData) => void;
  reject: (err: Error) => void;
};

let worker: Worker | null = null;
const pending = new Map<string, Pending>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./gcodeParser.worker.ts', import.meta.url), {
      type: 'module',
    });

    worker.onmessage = (e: MessageEvent) => {
      const { fileId, result, error } = e.data;
      const p = pending.get(fileId);
      if (!p) return;
      pending.delete(fileId);
      if (error) {
        p.reject(new Error(error));
      } else {
        p.resolve(result);
      }
    };

    worker.onerror = (err) => {
      // If the worker errors without a specific fileId, reject everything
      pending.forEach((p) => {
        p.reject(new Error(`Worker error: ${err.message}`));
      });
      pending.clear();
    };
  }
  return worker;
}

function generateId(): string {
  return `gc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Parse G-code content in a web worker and return extracted metadata.
 */
export async function parseGcodeInWorker(
  content: string
): Promise<GcodeExtractedData> {
  const fileId = generateId();
  const w = getWorker();

  return new Promise<GcodeExtractedData>((resolve, reject) => {
    pending.set(fileId, { resolve, reject });
    w.postMessage({ fileId, content });
  });
}

/**
 * Terminate the worker and clear pending requests.
 */
// fallow-ignore-next-line unused-export
export function terminateGcodeParser(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
  pending.forEach((p) => {
    p.reject(new Error('Parser worker terminated'));
  });
  pending.clear();
}
